import axios from 'axios';

// L'API est protégée : chaque appel doit porter le jeton de session.
// Ce module est importé une fois depuis index.js et configure l'instance
// axios partagée par toutes les pages — aucun appel existant n'a besoin
// d'être modifié.

// Remplacé par « /api » au déploiement (deploy/server-setup.sh).
export const API = '/api';

export const getToken = () => localStorage.getItem('token');
export const setToken = (t) => localStorage.setItem('token', t);
export const clearToken = () => localStorage.removeItem('token');

/**
 * Vrai si l'URL vise notre propre API.
 *
 * Le jeton ne doit être posé QUE sur nos appels. La première version de cet
 * intercepteur l'ajoutait à toutes les requêtes, y compris celles adressées à
 * des services extérieurs — la recherche d'entreprises de l'État, appelée
 * depuis le navigateur.
 *
 * Deux conséquences. D'abord une panne : un en-tête Authorization rend la
 * requête « non simple » au sens CORS, le navigateur demande donc une
 * autorisation préalable au serveur distant, que celui-ci refuse. La recherche
 * par nom ou SIREN ne renvoyait plus rien, silencieusement, le catch avalant
 * l'erreur. Ensuite un principe : on n'envoie pas le jeton de session d'un CRM
 * de courtage à un tiers, même à une administration. Il n'a jamais quitté le
 * navigateur ici — le blocage intervient avant l'envoi — mais l'intention
 * était fautive.
 */
function versNotreApi(url) {
  if (!url) return true;                     // requête relative
  if (url.startsWith('/')) return true;      // « /api/... » après déploiement
  if (url.startsWith(API)) return true;      // base explicite
  return !/^https?:\/\//i.test(url);        // toute autre absolue : un tiers
}

// ── Jeton ajouté aux seules requêtes vers notre API ─────────────────────────
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token && versNotreApi(config.url)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Session expirée ou invalide ──────────────────────────────────────────────
axios.interceptors.response.use(
  (reponse) => reponse,
  (erreur) => {
    const statut = erreur.response && erreur.response.status;
    const url = (erreur.config && erreur.config.url) || '';
    // Un 401 sur /login, c'est un mot de passe erroné : la page de connexion
    // affiche déjà le message, on ne déclenche pas la sortie de session.
    if (statut === 401 && !url.endsWith('/login')) {
      clearToken();
      window.dispatchEvent(new Event('session-expiree'));
    }
    return Promise.reject(erreur);
  }
);

/**
 * URL de téléchargement d'un document.
 *
 * Les documents sont ouverts par un lien <a href> : le navigateur fait la
 * requête lui-même et ne peut pas y placer d'en-tête Authorization. Le jeton
 * est donc passé en paramètre d'URL, seul cas où l'API l'accepte.
 */
export function urlFichier(chemin) {
  const token = getToken();
  if (!token) return chemin;
  return `${chemin}${chemin.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}
